'use client';
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import NewsletterSection from '@/components/NewsletterSection';
import Link from 'next/link';
import Image from 'next/image';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<'mandate' | 'vision' | 'mission'>('mandate');

  const coreValues = [
  {
    num: '01',
    label: 'Integrity',
    description: 'We uphold the highest standards of honesty, transparency, and ethical conduct in all our operations and relationships.',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>

  },
  {
    num: '02',
    label: 'Excellence',
    description: 'We strive for the highest quality in everything we do, continuously improving our processes, products, and services.',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>

  },
  {
    num: '03',
    label: 'Teamwork',
    description: 'We collaborate across boundaries, leveraging diverse perspectives and expertise to achieve shared goals.',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>

  },
  {
    num: '04',
    label: 'Innovation',
    description: 'We embrace new ideas, technologies, and approaches to solve complex challenges and create sustainable value.',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18h6" />
          <path d="M10 22h4" />
          <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
        </svg>

  },
  {
    num: '05',
    label: 'Sustainability',
    description: 'We are committed to responsible resource management that balances economic growth with environmental stewardship.',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 22 16 8" />
          <path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94z" />
          <path d="M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94z" />
          <path d="M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94z" />
        </svg>

  },
  {
    num: '06',
    label: 'National Pride',
    description: 'We are driven by a deep sense of purpose to develop Uganda\'s petroleum resources for the benefit of all Ugandans.',
    icon:
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
          <line x1="4" x2="4" y1="22" y2="15" />
        </svg>

  }];


  const subsidiaries = [
  {
    abbr: 'NPC',
    title: 'National Pipeline Company Ltd',
    description: "Wholly-owned subsidiary holding UNOC's 15% participating interest in the East African Crude Oil Pipeline (EACOP) project — the world's longest heated crude oil pipeline.",
    href: '/about/subsidiaries'
  },
  {
    abbr: 'URHC',
    title: 'Uganda Refinery Holdings Company Ltd',
    description: "Subsidiary established to hold UNOC's interests in the Kabaale Industrial Park and the proposed Uganda Oil Refinery, which will process up to 60,000 barrels per day.",
    href: '/about/subsidiaries'
  },
  {
    abbr: 'UEL',
    title: 'Uganda Energy Logistics Ltd',
    description: "Subsidiary engaged in the trading and distribution of petroleum products, building Uganda's downstream capacity and ensuring energy security.",
    href: '/about/subsidiaries'
  }];


  const leadershipPreview = [
  { initials: 'PN', name: 'Proscovia Nabbanja', title: 'Chief Executive Officer' },
  { initials: 'SM', name: 'Samantha Muhwezi Kassami', title: 'Chief Operating Officer' },
  { initials: 'EM', name: 'Emmanuel Mugagga', title: 'Chief Financial & Administrative Officer' },
  { initials: 'GK', name: 'Gilbert Kamuntu', title: 'Chief Commercial Officer' },
  { initials: 'PM', name: 'Peter Muliisa', title: 'Chief Legal Officer & Company Secretary' },
  { initials: 'CT', name: 'Catherine Tumusiime', title: 'Chief Human Resources Officer' }];


  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <Navbar />
      <main id="main-content" className="flex-1">

        {/* Hero Section */}
        <section className="bg-[#0F0F0F] pt-40 pb-20 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(204, 0, 0) 1px, transparent 0px)',
              backgroundSize: '40px 40px'
            }} />
          
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] pointer-events-none">
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-[#CC0000] blur-[120px] rounded-full" />
          </div>
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative">
            <nav className="flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-gray-500 mb-8" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-[#CC0000] transition-colors">Home</Link>
              <span className="text-[#CC0000]">/</span>
              <span className="text-[#CC0000]">About Us</span>
            </nav>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">About UNOC</span>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-[1.05] tracking-tight max-w-4xl">
              Driving Uganda&apos;s<br />Petroleum Future
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed mb-10">
              The Uganda National Oil Company (UNOC) is a state-owned enterprise established under the Petroleum (Exploration, Development and Production) Act, 2013, to handle the State&apos;s commercial interests in the petroleum sub-sector.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/about/mandate"
                className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:bg-[#AA0000] transition-colors group">
                
                Our Mandate
                <span className="bg-white/15 group-hover:bg-white/25 p-1.5 rounded-sm transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></svg>
                </span>
              </Link>
              <Link
                href="/leadership"
                className="inline-flex items-center gap-2 border border-white/20 text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:border-[#CC0000] hover:text-[#CC0000] transition-colors group">
                
                Leadership Team
                <span className="bg-white/10 group-hover:bg-[#CC0000]/10 p-1.5 rounded-sm transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></svg>
                </span>
              </Link>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#CC0000] to-transparent opacity-50" />
        </section>

        {/* Hero Banner Image */}
        <section className="bg-white pt-16">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="relative overflow-hidden bg-[#0F0F0F]">
              <img
                src="https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg"
                alt="UNOC 10th anniversary celebration event with staff and government officials Uganda petroleum sector"
                className="w-full h-[320px] sm:h-[420px] lg:h-[520px] object-cover opacity-70" />
              
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F]/90 via-[#0F0F0F]/40 to-transparent pointer-events-none" />
              <div className="absolute top-6 left-6 w-10 h-10 border-l-2 border-t-2 border-[#CC0000]" />
              <div className="absolute bottom-6 right-6 w-10 h-10 border-r-2 border-b-2 border-[#CC0000]" />
              <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-14 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-[1px] w-8 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Our Story</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold max-w-3xl leading-[1.1] tracking-tight">
                  A national company driving Uganda&apos;s energy ambitions
                </h2>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Are — Two Column */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
                  <div className="h-[1px] w-12 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Who We Are</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0F0F0F] mb-6 leading-[1.05] tracking-tight">
                  Joint Ventures Across{' '}
                  <span className="text-[#CC0000]">the Value Chain</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-5 text-lg">
                  UNOC participates in Uganda&apos;s petroleum sector through a network of joint ventures and subsidiaries spanning the entire value chain — from upstream exploration and production (Tilenga, KFDA, New Ventures) to midstream pipelines (EACOP, NPC), refining (URHC, KIP, Refinery), and downstream bulk trading and storage.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg mb-8">
                  Our partners include leading global operators and service companies — TotalEnergies, CNOOC, Saipem, Baker Hughes, and others — all contributing to Uganda&apos;s emergence as an oil-producing nation.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                  { value: '6.5B', label: 'Barrels of Oil Reserves' },
                  { value: '1,443km', label: 'EACOP Pipeline Length' },
                  { value: '60K', label: 'Barrels Per Day Capacity' },
                  { value: '10+', label: 'Years of Operations' }].
                  map((stat) =>
                  <div key={stat.label} className="border border-gray-200 p-5 bg-[#F8F9FA]">
                      <div className="text-2xl font-extrabold text-[#CC0000] mb-1">{stat.value}</div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">{stat.label}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="relative">
                <div className="bg-[#0F0F0F] p-1">
                  <img
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_15e6045ef-1778365791005.png"
                    alt="East African Crude Oil Pipeline EACOP infrastructure Uganda Tanzania midstream operations"
                    className="w-full h-[480px] object-cover opacity-90" />
                  
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#CC0000] flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="text-2xl font-extrabold leading-none">13+</div>
                    <div className="text-[9px] uppercase tracking-wider mt-1 font-medium">JV Partners</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mandate / Vision / Mission Tabs */}
        <section className="py-24 bg-[#0F0F0F] relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(204, 0, 0) 1px, transparent 0px)',
              backgroundSize: '40px 40px'
            }} />
          
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 02 ]</span>
                <div className="h-[1px] w-12 bg-[#CC0000]" />
                <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Purpose</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
                Our <span className="text-[#CC0000]">Mandate</span>
              </h2>
            </div>

            {/* Tab Buttons */}
            <div className="flex border-b border-white/10 mb-12 gap-0">
              {(['mandate', 'vision', 'mission'] as const).map((tab) =>
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-4 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[2px] ${
                activeTab === tab ?
                'border-[#CC0000] text-[#CC0000]' :
                'border-transparent text-gray-500 hover:text-white'}`
                }>
                
                  {tab === 'mandate' ? 'Our Mandate' : tab === 'vision' ? 'Our Vision' : 'Our Mission'}
                </button>
              )}
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                {activeTab === 'mandate' &&
                <div>
                    <h3 className="text-3xl font-extrabold text-white mb-6">Our Mandate</h3>
                    <p className="text-gray-300 text-lg leading-relaxed mb-6">
                      To handle the State&apos;s commercial interests in the petroleum sub-sector and ensure that the resource is exploited in a sustainable manner for the benefit of all Ugandans.
                    </p>
                    <p className="text-gray-400 leading-relaxed">
                      UNOC was established under the Petroleum (Exploration, Development and Production) Act, 2013 and the Petroleum (Refining, Gas Processing and Conversion, Transportation and Storage) Act, 2013 to participate in petroleum activities on behalf of the Government of Uganda.
                    </p>
                  </div>
                }
                {activeTab === 'vision' &&
                <div>
                    <h3 className="text-3xl font-extrabold text-white mb-6">Our Vision</h3>
                    <p className="text-gray-300 text-lg leading-relaxed mb-6">
                      A vibrant and profitable Oil and Gas Company contributing to the socio-economic transformation of Uganda.
                    </p>
                    <p className="text-gray-400 leading-relaxed">
                      We envision a Uganda where petroleum resources are leveraged to drive industrialization, create employment, generate government revenue, and improve the quality of life for all citizens.
                    </p>
                  </div>
                }
                {activeTab === 'mission' &&
                <div>
                    <h3 className="text-3xl font-extrabold text-white mb-6">Our Mission</h3>
                    <p className="text-gray-300 text-lg leading-relaxed mb-6">
                      To sustainably and commercially manage the State&apos;s commercial interests in oil and gas.
                    </p>
                    <p className="text-gray-400 leading-relaxed">
                      We achieve this by participating in joint ventures, managing subsidiaries, building technical capacity, and ensuring that Uganda&apos;s petroleum resources generate maximum value for the nation.
                    </p>
                  </div>
                }
                <Link
                  href="/about/mandate"
                  className="inline-flex items-center gap-2 mt-8 bg-[#CC0000] text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:bg-[#AA0000] transition-colors group">
                  
                  Read Full Mandate
                  <span className="bg-white/15 group-hover:bg-white/25 p-1.5 rounded-sm transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></svg>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-0 border-t border-l border-white/10">
                {[
                { label: 'Established', value: '2015', desc: 'Under the Petroleum Acts of 2013' },
                { label: 'Headquarters', value: 'Kampala', desc: 'Plot 15 Yusuf Lule Road, Uganda' },
                { label: 'Ownership', value: '100%', desc: 'Government of Uganda owned' },
                { label: 'Sector', value: 'Energy', desc: 'Oil & Gas across full value chain' }].
                map((item) =>
                <div key={item.label} className="border-r border-b border-white/10 p-6 flex items-center gap-6">
                    <div className="w-16 h-16 bg-[#CC0000]/10 border border-[#CC0000]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#CC0000] font-extrabold text-sm">{item.value}</span>
                    </div>
                    <div>
                      <div className="text-white font-bold text-sm mb-1">{item.label}</div>
                      <div className="text-gray-500 text-xs">{item.desc}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 03 ]</span>
                <div className="h-[1px] w-12 bg-[#CC0000]" />
                <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Core Values</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight">
                What <span className="text-[#CC0000]">Guides Us</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-gray-200">
              {coreValues.map((value) =>
              <div
                key={value.label}
                className="bg-[#F8F9FA] border-r border-b border-gray-200 p-8 hover:bg-[#0F0F0F] group transition-colors duration-300">
                
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-[#CC0000] group-hover:text-[#CC0000] transition-colors">
                      {value.icon}
                    </div>
                    <span className="font-mono text-xs text-gray-300 group-hover:text-white/30 tracking-[0.2em] transition-colors">
                      {value.num} / 06
                    </span>
                  </div>
                  <h3 className="text-xl font-extrabold text-[#0F0F0F] group-hover:text-white font-heading mb-3 transition-colors">
                    {value.label}
                  </h3>
                  <p className="text-sm text-gray-500 group-hover:text-gray-400 leading-relaxed transition-colors">
                    {value.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Leadership Preview */}
        <section className="py-24 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 04 ]</span>
                  <div className="h-[1px] w-12 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Leadership</span>
                </div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight">
                  Leadership <span className="text-[#CC0000]">Team</span>
                </h2>
                <p className="text-lg text-gray-600 mt-5">
                  Meet the executives leading UNOC&apos;s mission to transform Uganda&apos;s petroleum sector.
                </p>
              </div>
              <Link
                href="/leadership"
                className="inline-flex items-center gap-2 bg-[#CC0000] text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:bg-[#AA0000] transition-colors group">
                
                View Full Profiles
                <span className="bg-white/15 group-hover:bg-white/25 transition-colors p-1.5 rounded-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></svg>
                </span>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-gray-200">
              {leadershipPreview.map((member) =>
              <div
                key={member.initials}
                className="bg-white border-r border-b border-gray-200 p-8 hover:bg-[#0F0F0F] group transition-colors duration-300">
                
                  <div className="w-16 h-16 bg-[#F8F9FA] border border-gray-200 flex items-center justify-center text-xl font-extrabold text-[#0F0F0F] mb-6 group-hover:bg-[#CC0000] group-hover:text-white group-hover:border-[#CC0000] transition-colors duration-300">
                    {member.initials}
                  </div>
                  <h3 className="text-xl font-extrabold text-[#0F0F0F] group-hover:text-white mb-1 transition-colors">
                    {member.name}
                  </h3>
                  <p className="text-sm text-[#CC0000] font-bold uppercase tracking-wider">
                    {member.title}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Subsidiaries Section */}
        <section id="subsidiaries" className="py-24 bg-[#0F0F0F] text-white relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(204, 0, 0) 1px, transparent 0px)',
              backgroundSize: '40px 40px'
            }} />
          
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative">
            <div className="mb-16 flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 05 ]</span>
                  <div className="h-[1px] w-12 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Subsidiaries</span>
                </div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
                  Our <span className="text-[#CC0000]">Subsidiaries</span>
                </h2>
                <p className="text-gray-400 text-lg mt-5">
                  UNOC operates through wholly-owned subsidiaries that manage specific segments of the petroleum value chain.
                </p>
              </div>
              <Link
                href="/about/subsidiaries"
                className="inline-flex items-center gap-2 border border-white/20 text-white pl-5 pr-2 py-2.5 rounded-sm text-sm font-semibold hover:border-[#CC0000] hover:text-[#CC0000] transition-colors group">
                
                View All Subsidiaries
                <span className="bg-white/10 group-hover:bg-[#CC0000]/10 p-1.5 rounded-sm transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></svg>
                </span>
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-0 border-t border-l border-white/10">
              {subsidiaries.map((sub) =>
              <Link
                key={sub.abbr}
                href={sub.href}
                className="block bg-transparent border-r border-b border-white/10 p-10 hover:bg-[#CC0000] group transition-colors duration-300">
                
                  <div className="w-14 h-14 bg-white/5 border border-white/10 flex items-center justify-center text-lg font-extrabold text-[#CC0000] group-hover:text-white group-hover:bg-white/10 group-hover:border-white/20 mb-6 transition-colors">
                    {sub.abbr}
                  </div>
                  <h3 className="text-xl font-extrabold mb-4 group-hover:text-white transition-colors">
                    {sub.title}
                  </h3>
                  <p className="text-gray-400 group-hover:text-white/90 leading-relaxed transition-colors text-sm">
                    {sub.description}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-[#CC0000] group-hover:text-white text-xs font-bold uppercase tracking-wider transition-colors">
                    <span>Learn More</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 7h10v10" /><path d="M7 17 17 7" /></svg>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* History Timeline */}
        <section className="py-24 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-5">
                <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 06 ]</span>
                <div className="h-[1px] w-12 bg-[#CC0000]" />
                <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em]">Our History</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F0F0F] leading-[1.05] tracking-tight">
                Key <span className="text-[#CC0000]">Milestones</span>
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-[18px] top-0 bottom-0 w-[2px] bg-gray-200 hidden md:block" />
              <div className="space-y-0">
                {[
                { year: '2006', title: 'Oil Discovery', desc: 'Commercial oil discovery in the Albertine Graben, Lake Albert region of Uganda.' },
                { year: '2013', title: 'Legal Framework', desc: 'Petroleum Acts of 2013 enacted, establishing the legal basis for UNOC\'s creation.' },
                { year: '2015', title: 'UNOC Established', desc: 'Uganda National Oil Company officially incorporated to manage the State\'s commercial interests.' },
                { year: '2018', title: 'Subsidiaries Formed', desc: 'NPC and URHC established as wholly-owned subsidiaries to manage pipeline and refinery interests.' },
                { year: '2022', title: 'FID Achieved', desc: 'Final Investment Decision reached for the Tilenga and Kingfisher projects and the EACOP pipeline.' },
                { year: '2025+', title: 'First Oil', desc: 'Uganda on track for first oil production, marking a transformative moment for the nation.' }].
                map((milestone, idx) =>
                <div key={milestone.year} className="relative flex gap-8 md:gap-16 items-start pb-0 border-b border-gray-100 last:border-0">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#CC0000] flex items-center justify-center relative z-10 mt-6">
                      <div className="w-2 h-2 bg-white rounded-full" />
                    </div>
                    <div className="flex-1 py-6">
                      <div className="flex flex-wrap items-center gap-4 mb-2">
                        <span className="font-mono text-xs text-[#CC0000] font-bold tracking-[0.2em]">{milestone.year}</span>
                        <h3 className="text-lg font-extrabold text-[#0F0F0F]">{milestone.title}</h3>
                      </div>
                      <p className="text-gray-500 text-sm leading-relaxed">{milestone.desc}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

      </main>
      <NewsletterSection />
      <Footer />
    </div>);

}